import { useApiRequest } from '~/hooks/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import extractFullName from '~/utils/text/extractFullName';
import AllocationTypes from '../../constants/allocationTypes';

interface UseGetEditClientAllocationsParams {
  actTeamId: string;
  clientAllocationDate: string;
  isCrisis: boolean;
}

interface UseGetEditClientAllocationsResult {
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const useGetEditClientAllocations = ({
  actTeamId,
  clientAllocationDate,
  isCrisis,
}: UseGetEditClientAllocationsParams): UseGetEditClientAllocationsResult => {
  const {
    clinic: userClinic,
    id: userId,
    photo,
    ...user
  } = useStoreState(state => state.user.current);
  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetClinicActTeam, onGetActTeamsAvailable } = useStoreActions(
    actions => actions.actTeam,
  );
  const { onGetClientAllocation, onGetOwnClientAllocation, onGetOwnClientAllocationGU } =
    useStoreActions(actions => actions.clientAllocation);

  const { isActTeamMember, isGlobalUser } = useRole();
  const userFullName = extractFullName(user);

  const { isLoading, refetch } = useApiRequest(
    async () => {
      const clinicId = String(userClinic.id);
      const allocationDate = `${clientAllocationDate}T00:00:00Z`;

      // Step 1: Fetch clinic and team data
      await onGetMyClinic();
      await (isGlobalUser
        ? onGetActTeamsAvailable({ clinicId })
        : onGetClinicActTeam({ clinicId, actTeamId }));

      // Step 2: Fetch client allocations
      if (isGlobalUser) {
        await onGetOwnClientAllocationGU({
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
      } else if (isActTeamMember) {
        await onGetOwnClientAllocation({
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
      } else {
        await onGetClientAllocation({
          clinicId,
          teamId: actTeamId,
          clientAllocationDate: allocationDate,
          allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
        });
      }
    },
    {
      dependencies: [
        actTeamId,
        clientAllocationDate,
        isActTeamMember,
        isGlobalUser,
        userClinic.id,
        userId,
        userFullName,
        photo,
      ],
    },
  );

  return {
    isLoading,
    refetch,
  };
};

export default useGetEditClientAllocations;
